import { useForm, useStore } from '@tanstack/react-form'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, TextField } from '../../../rapid-ui'
import { SettingsSubheading } from '../shared/settings-typography'
import { CompanyPrivacyPolicyViewModel } from './company-privacy-policy.vm'

export interface CompanyPrivacyPolicyProps {
	viewModel: CompanyPrivacyPolicyViewModel
}

export function CompanyPrivacyPolicy({ viewModel }: CompanyPrivacyPolicyProps) {
	const { t } = useTranslation('common')
	const vm = viewModel

	// Track mode separately for instant radio button switching
	const [useCustomPolicy, setUseCustomPolicy] = useState(!!vm.view.privacyPolicyLink)

	// Sync local state with saved value when VM changes (e.g., after page load with saved data)
	useEffect(() => {
		setUseCustomPolicy(!!vm.view.privacyPolicyLink)
	}, [vm.view.privacyPolicyLink])

	const form = useForm({
		defaultValues: {
			privacyPolicyLink: vm.view.privacyPolicyLink,
			dpoName: vm.view.dpoName,
			dpoPhone: vm.view.dpoPhone,
			dpoEmail: vm.view.dpoEmail,
			legalEntityName: vm.view.legalEntityName,
			orgStreetAddress: vm.view.orgStreetAddress,
			orgPostalCode: vm.view.orgPostalCode,
			orgCity: vm.view.orgCity,
			orgCountry: vm.view.orgCountry,
			orgPhone: vm.view.orgPhone,
			orgEmail: vm.view.orgEmail,
		},
		onSubmit: async ({ value }) => {
			vm.events.onSavePrivacySettings(value)
		},
		validators: {
			onChange: ({ value }) => {
				const errors: Record<string, string> = {}

				if (value.privacyPolicyLink?.length > 0) {
					if (!z.string().url().safeParse(value.privacyPolicyLink).success) {
						errors.privacyPolicyLink = t('settings.companyPrivacyPolicy.errorInvalidUrl')
					}
				}

				if (value.dpoEmail?.length > 0) {
					if (!z.string().email().safeParse(value.dpoEmail).success) {
						errors.dpoEmail = t('settings.companyPrivacyPolicy.errorInvalidEmail')
					}
				}

				if (value.dpoPhone?.length > 0) {
					const phoneRegex = /^[\d\s\-+()]+$/
					if (!phoneRegex.test(value.dpoPhone)) {
						errors.dpoPhone = t('settings.companyPrivacyPolicy.errorInvalidPhone')
					}
				}

				if (value.orgEmail?.length > 0) {
					if (!z.string().email().safeParse(value.orgEmail).success) {
						errors.orgEmail = t('settings.companyPrivacyPolicy.errorInvalidEmail')
					}
				}

				if (value.orgPhone?.length > 0) {
					const phoneRegex = /^[\d\s\-+()]+$/
					if (!phoneRegex.test(value.orgPhone)) {
						errors.orgPhone = t('settings.companyPrivacyPolicy.errorInvalidPhone')
					}
				}

				return Object.keys(errors).length > 0 ? { fields: errors } : undefined
			},
		},
	})

	const isSubmitting = useStore(form.store, (formState) => formState.isSubmitting)
	const canSubmit = useStore(form.store, (formState) => formState.canSubmit)

	const privacyPolicyLink = `/company/${vm.view.companyId}/privacy-policy`

	if (vm.loading.privacyPolicyLink) {
		return <div>{t('common.loading')}</div>
	}

	return (
		<section className="space-y-6">
			<form
				onSubmit={(e) => {
					e.preventDefault()
					void form.handleSubmit()
				}}
				className="space-y-8"
			>
				{/* Intro Paragraph */}
				<p className="text-gray-700">
					{t('settings.companyPrivacyPolicy.intro')}
				</p>

				{/* Privacy Policy Link Section */}
				<div className="space-y-6">
					<SettingsSubheading>{t('settings.companyPrivacyPolicy.privacyPolicyHeading')}</SettingsSubheading>
					<div className="space-y-4 mb-6">
						<p className="text-gray-700">
							{t('settings.companyPrivacyPolicy.candidatePrompt')}{' '}
							<a
								href="https://www.jobilla.com/en/terms-of-service-job-seekers"
								className="text-brand-700 decoration-2 underline decoration-brand-300"
								target="_blank"
								rel="noopener noreferrer"
							>
								{t('settings.companyPrivacyPolicy.termsOfService')}
							</a>{' '}
							{t('settings.companyPrivacyPolicy.choosePolicy')}
						</p>
					</div>

					{/* Radio Button Group for Mode Selection */}
					<div className="space-y-4">
						<label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
							<input
								type="radio"
								name="privacyPolicyMode"
								checked={!useCustomPolicy}
								onChange={() => {
									setUseCustomPolicy(false)
									form.setFieldValue('privacyPolicyLink', '')
								}}
								className="mt-1 w-4 h-4 text-brand-600 border-gray-300 focus:ring-brand-500"
							/>
							<div className="flex-1">
								<div className="font-medium text-gray-900">
									{t('settings.companyPrivacyPolicy.useGenerated')}
								</div>
								<p className="text-sm text-gray-600 mt-1">
									{t('settings.companyPrivacyPolicy.useGeneratedDescription')}
								</p>
							</div>
						</label>

						<label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
							<input
								type="radio"
								name="privacyPolicyMode"
								checked={useCustomPolicy}
								onChange={() => {
									setUseCustomPolicy(true)
								}}
								className="mt-1 w-4 h-4 text-brand-600 border-gray-300 focus:ring-brand-500"
							/>
							<div className="flex-1">
								<div className="font-medium text-gray-900">
									{t('settings.companyPrivacyPolicy.useCustom')}
								</div>
								<p className="text-sm text-gray-600 mt-1">
									{t('settings.companyPrivacyPolicy.useCustomDescription')}
								</p>
								{useCustomPolicy && (
									<p className="text-sm text-orange-600 mt-2 font-medium">
										⚠️ {t('settings.companyPrivacyPolicy.customWarning')}
									</p>
								)}
							</div>
						</label>
					</div>

					{/* Conditional URL Input Field */}
					{useCustomPolicy && (
						<form.Field name="privacyPolicyLink">
							{({ state, handleChange, handleBlur }) => (
								<TextField
									value={state.value}
									onChange={handleChange}
									onBlur={handleBlur}
									label={t('settings.companyPrivacyPolicy.customUrlLabel')}
									placeholder={t('settings.companyPrivacyPolicy.customUrlPlaceholder')}
									hint={t('settings.companyPrivacyPolicy.customUrlHint')}
									isWaiting={vm.loading.privacyPolicySaving}
									errors={[...state.meta.errors, vm.errors.privacyPolicyLink]}
								/>
							)}
						</form.Field>
					)}
				</div>

				{/* Organization Information Section - Hidden when using custom URL */}
				{!useCustomPolicy && (
					<>
						<div className="space-y-6">
							<SettingsSubheading>
								{t('settings.companyPrivacyPolicy.organizationHeading')}
							</SettingsSubheading>
							<div className="space-y-4 mb-6">
								<p className="text-gray-700">
									{t('settings.companyPrivacyPolicy.organizationIntro')}
								</p>
							</div>

							{/* Organization Fields */}
							<div className="space-y-6">
								<form.Field name="legalEntityName">
									{({ state, handleChange, handleBlur }) => (
										<TextField
											value={state.value}
											onChange={handleChange}
											onBlur={handleBlur}
											label={t('settings.companyPrivacyPolicy.companyNameLabel')}
											hint={t('settings.companyPrivacyPolicy.companyNameHint')}
											isWaiting={vm.loading.privacyPolicySaving}
											errors={[...state.meta.errors, vm.errors.legalEntityName]}
										/>
									)}
								</form.Field>

								<form.Field name="orgStreetAddress">
									{({ state, handleChange, handleBlur }) => (
										<TextField
											value={state.value}
											onChange={handleChange}
											onBlur={handleBlur}
											label={t('settings.companyPrivacyPolicy.streetAddressLabel')}
											hint={t('settings.companyPrivacyPolicy.streetAddressHint')}
											isWaiting={vm.loading.privacyPolicySaving}
											errors={[...state.meta.errors, vm.errors.orgStreetAddress]}
										/>
									)}
								</form.Field>

								<div className="grid grid-cols-2 gap-4">
									<form.Field name="orgPostalCode">
										{({ state, handleChange, handleBlur }) => (
											<TextField
												value={state.value}
												onChange={handleChange}
												onBlur={handleBlur}
												label={t('settings.companyPrivacyPolicy.postalCodeLabel')}
												hint={t('settings.companyPrivacyPolicy.postalCodeHint')}
												isWaiting={vm.loading.privacyPolicySaving}
												errors={[...state.meta.errors, vm.errors.orgPostalCode]}
											/>
										)}
									</form.Field>

									<form.Field name="orgCity">
										{({ state, handleChange, handleBlur }) => (
											<TextField
												value={state.value}
												onChange={handleChange}
												onBlur={handleBlur}
												label={t('settings.companyPrivacyPolicy.cityLabel')}
												hint={t('settings.companyPrivacyPolicy.cityHint')}
												isWaiting={vm.loading.privacyPolicySaving}
												errors={[...state.meta.errors, vm.errors.orgCity]}
											/>
										)}
									</form.Field>
								</div>

								<form.Field name="orgCountry">
									{({ state, handleChange, handleBlur }) => (
										<TextField
											value={state.value}
											onChange={handleChange}
											onBlur={handleBlur}
											label={t('settings.companyPrivacyPolicy.countryLabel')}
											hint={t('settings.companyPrivacyPolicy.countryHint')}
											isWaiting={vm.loading.privacyPolicySaving}
											errors={[...state.meta.errors, vm.errors.orgCountry]}
										/>
									)}
								</form.Field>

								<form.Field name="orgPhone">
									{({ state, handleChange, handleBlur }) => (
										<TextField
											value={state.value}
											onChange={handleChange}
											onBlur={handleBlur}
											type="text"
											label={t('settings.companyPrivacyPolicy.phoneLabel')}
											placeholder="+1 234 567 8900"
											hint={t('settings.companyPrivacyPolicy.phoneHint')}
											isWaiting={vm.loading.privacyPolicySaving}
											errors={[...state.meta.errors, vm.errors.orgPhone]}
										/>
									)}
								</form.Field>

								<form.Field name="orgEmail">
									{({ state, handleChange, handleBlur }) => (
										<TextField
											value={state.value}
											onChange={handleChange}
											onBlur={handleBlur}
											type="email"
											label={t('settings.companyPrivacyPolicy.emailLabel')}
											placeholder="contact@company.com"
											hint={t('settings.companyPrivacyPolicy.emailHint')}
											isWaiting={vm.loading.privacyPolicySaving}
											errors={[...state.meta.errors, vm.errors.orgEmail]}
										/>
									)}
								</form.Field>
							</div>
						</div>

						{/* Data Protection Officer Section */}
						<div className="space-y-6">
							<SettingsSubheading>{t('settings.companyPrivacyPolicy.dpoHeading')}</SettingsSubheading>
							<div className="space-y-4 mb-6">
								<p className="text-gray-700">
									{t('settings.companyPrivacyPolicy.dpoIntro')}
								</p>
								<p>
									<Button
										type={'subtle'}
										size={'tiny'}
										postfix={'popout'}
										onClick={() => {
											const url = new URL('https://gdpr.eu/data-protection-officer/')
											window.open(url.toString(), '_blank')
											// rel="noopener noreferrer"
										}}
										// className="text-brand-700 decoration-2 underline decoration-brand-300"
									>
										{t('settings.companyPrivacyPolicy.dpoReadMore')}
									</Button>
								</p>
							</div>

							{/* DPO Fields */}
							<div className="space-y-6">
								<form.Field name="dpoName">
									{({ state, handleChange, handleBlur }) => (
										<TextField
											value={state.value}
											onChange={handleChange}
											onBlur={handleBlur}
											label={t('settings.companyPrivacyPolicy.dpoNameLabel')}
											hint={t('settings.companyPrivacyPolicy.dpoNameHint')}
											isWaiting={vm.loading.privacyPolicySaving}
											errors={[...state.meta.errors, vm.errors.dpoName]}
										/>
									)}
								</form.Field>

								<form.Field name="dpoPhone">
									{({ state, handleChange, handleBlur }) => (
										<TextField
											value={state.value}
											onChange={handleChange}
											onBlur={handleBlur}
											type="text"
											label={t('settings.companyPrivacyPolicy.dpoPhoneLabel')}
											placeholder="+1 234 567 8900"
											hint={t('settings.companyPrivacyPolicy.dpoPhoneHint')}
											isWaiting={vm.loading.privacyPolicySaving}
											errors={[...state.meta.errors, vm.errors.dpoPhone]}
										/>
									)}
								</form.Field>

								<form.Field name="dpoEmail">
									{({ state, handleChange, handleBlur }) => (
										<TextField
											value={state.value}
											onChange={handleChange}
											onBlur={handleBlur}
											type="email"
											label={t('settings.companyPrivacyPolicy.dpoEmailLabel')}
											placeholder="dpo@company.com"
											hint={t('settings.companyPrivacyPolicy.dpoEmailHint')}
											isWaiting={vm.loading.privacyPolicySaving}
											errors={[...state.meta.errors, vm.errors.dpoEmail]}
										/>
									)}
								</form.Field>
							</div>
						</div>
					</>
				)}

				{/* Single Save Button */}
				<div className="pt-6 flex gap-x-2">
					<Button
						type="subtle"
						size="short"
						onClick={() => {
							const customUrl = form.state.values.privacyPolicyLink
							// If custom mode and URL is valid, open it in new tab
							if (useCustomPolicy && customUrl && z.string().url().safeParse(customUrl).success) {
								window.open(customUrl, '_blank', 'noopener,noreferrer')
							} else {
								// Otherwise preview the generated policy in new tab
								window.open(privacyPolicyLink, '_blank', 'noopener,noreferrer')
							}
						}}
						disabled={isSubmitting || vm.loading.privacyPolicySaving}
						postfix="popout"
					>
						{useCustomPolicy
							? t('settings.companyPrivacyPolicy.previewCustom')
							: t('settings.companyPrivacyPolicy.previewGenerated')}
					</Button>
					<Button
						type="committal"
						size="short"
						submit={true}
						disabled={!canSubmit || isSubmitting}
						isLoading={vm.loading.privacyPolicySaving}
					>
						{t('settings.companyPrivacyPolicy.saveSettings')}
					</Button>
				</div>
			</form>
		</section>
	)
}
